/**
 * Live handles to every mounted shape-text frame, keyed by shape id.
 *
 * Cross-frame text selection (WMPCM-9199) needs to resolve a caret inside a frame *other* than the
 * one the drag started in, and only that frame's own `ShapeTextEditor` holds the layout to do it
 * with — `layout.carets` is derived per frame from its own flow, shape outline and wrap bands.
 * Recomputing a sibling's layout from the dragging frame would duplicate the whole scanline engine
 * and risk measuring with a different one than paints, which is the invariant
 * `fitParagraphsToShape` exists to protect.
 *
 * A module-level map rather than a context: the registry is written from a `useEffect` in every
 * editor and read from a window-level pointermove handler, so a re-render-driven context would add
 * nothing but would force the reader to be a component. Registration is ref-counted by mount, so a
 * spread switch (which unmounts every editor) leaves the map empty.
 */
import type { ShapeTextFrameHandle } from './types/shape-text-frame-handle'

const frames = new Map<string, ShapeTextFrameHandle>()

/**
 * A change signal for readers that follow what the frames hold (the spell check). It counts every
 * registration, removal and reported change, so a reader can subscribe, re-read on change, and
 * skip its own work while nothing moved. Shaped for `useSyncExternalStore`.
 */
let revision = 0
const listeners = new Set<() => void>()

/** Tell subscribers that a frame was added or removed, or that a frame's text, caret or overflow changed. */
export function notifyShapeTextFramesChanged(): void {
  revision += 1
  listeners.forEach((listener) => listener())
}

export function subscribeToShapeTextFrames(listener: () => void): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export function getShapeTextFramesRevision(): number {
  return revision
}

/** Registers a frame and returns the matching unregister, for a `useEffect` cleanup. */
export function registerShapeTextFrame(shapeId: string, handle: ShapeTextFrameHandle): () => void {
  frames.set(shapeId, handle)
  notifyShapeTextFramesChanged()

  return () => {
    // Guard against a remount having already replaced this entry: React can mount the next
    // instance before running the previous one's cleanup, and deleting then would drop the live
    // handle rather than the stale one.
    if (frames.get(shapeId) === handle) {
      frames.delete(shapeId)
      notifyShapeTextFramesChanged()
    }
  }
}

export function getShapeTextFrame(shapeId: string): ShapeTextFrameHandle | undefined {
  return frames.get(shapeId)
}

/**
 * The chain member under a client point, if any.
 *
 * Ordered by `frameIds` (flow order) rather than by DOM hit-testing, because sibling frames are
 * `pointerEvents: 'none'` while not editing — `document.elementFromPoint` would return the canvas
 * beneath them, which is exactly why the drag could not leave its origin frame before.
 */
export function chainFrameAtClientPoint(
  frameIds: readonly string[],
  clientX: number,
  clientY: number
): { frameId: string; index: number } | null {
  for (let i = 0; i < frameIds.length; i++) {
    const id = frameIds[i]!

    if (getShapeTextFrame(id)?.containsClientPoint(clientX, clientY)) {
      return { frameId: id, index: i }
    }
  }

  return null
}

/** Plain-text lengths for a chain, in flow order. Unregistered members contribute 0. */
export function chainFrameTextLengths(frameIds: readonly string[]): number[] {
  return frameIds.map((id) => getShapeTextFrame(id)?.getTextLength() ?? 0)
}

/**
 * Ids, among `frameIds`, whose frame is painting its overflow badge right now. Unregistered frames
 * are absent, which is the right default: no editor mounted means no badge drawn.
 */
export function framesShowingOverflowBadge(frameIds: readonly string[]): Set<string> {
  const showing = new Set<string>()

  for (const id of frameIds) {
    if (getShapeTextFrame(id)?.isShowingOverflowBadge()) showing.add(id)
  }

  return showing
}

/** Test-only: drop every registration. */
export function resetShapeTextFrameRegistry(): void {
  frames.clear()
}
